import { useEffect, useMemo, useState } from "react";
import client from "../api/client";

const styles = {
  page: { fontSize: 12 },
  card: {
    border: 0,
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: "0 2px 12px rgba(0,0,0,.08)",
    background: "#fff",
  },
  header: {
    background: "linear-gradient(90deg,#0b4f4a,#0f766e)",
    color: "#fff",
    padding: "10px 14px",
  },
  title: { fontSize: 15, fontWeight: 800, margin: 0 },
  sub: { fontSize: 11, opacity: 0.88 },
  body: { padding: 12 },
  label: { fontSize: 11, fontWeight: 700, marginBottom: 3 },
  input: { height: 32, fontSize: 12, borderRadius: 8 },
  btn: { height: 32, fontSize: 12, borderRadius: 8, fontWeight: 700 },
  th: {
    fontSize: 11,
    background: "#07111f",
    color: "#fff",
    whiteSpace: "nowrap",
  },
  td: { fontSize: 12, verticalAlign: "middle", whiteSpace: "nowrap" },
  stat: {
    border: "1px solid #e9ecef",
    borderRadius: 12,
    padding: 10,
    background: "#f8f9fa",
  },
};

const emptyLine = { hsnCode: "", taxableAmount: 0, gstPercent: 18 };

export default function GstVoucherPage() {
  const companyIdFromStorage = Number(localStorage.getItem("companyId") || 1);

  const [accounts, setAccounts] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    companyId: companyIdFromStorage,
    voucherNo: "GST-001",
    voucherDate: new Date().toISOString().slice(0, 10),
    voucherType: "OUTPUT",
    supplyType: "INTRA",
    partyAccountId: "",
    taxableAccountId: "",
    narration: "",
    details: [{ ...emptyLine }],
  });

  const getId = (x) => x?.Id ?? x?.id;
  const getCode = (x) => x?.AccountCode ?? x?.accountCode ?? "";
  const getName = (x) => x?.AccountName ?? x?.accountName ?? "";

  const load = async () => {
    try {
      setLoading(true);

      const [accRes, voucherRes] = await Promise.all([
        client.get("/accounts", {
          params: form.companyId ? { companyId: form.companyId } : {},
        }),
        client.get("/gstvouchers", {
          params: form.companyId ? { companyId: form.companyId } : {},
        }),
      ]);

      setAccounts(Array.isArray(accRes.data) ? accRes.data : []);
      setRows(Array.isArray(voucherRes.data) ? voucherRes.data : []);
    } catch (e) {
      alert(
        e?.response?.data?.message ||
          e?.response?.data ||
          e.message ||
          "Unable to load GST vouchers",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.companyId]);

  const setLine = (i, key, value) => {
    setForm((prev) => ({
      ...prev,
      details: prev.details.map((line, index) =>
        index === i ? { ...line, [key]: value } : line,
      ),
    }));
  };

  const removeLine = (index) => {
    if (form.details.length === 1) return;

    setForm((prev) => ({
      ...prev,
      details: prev.details.filter((_, i) => i !== index),
    }));
  };

  const totals = useMemo(() => {
    return form.details.reduce(
      (acc, line) => {
        const taxable = Number(line.taxableAmount || 0);
        const gst = (taxable * Number(line.gstPercent || 0)) / 100;

        const cgst = form.supplyType === "INTRA" ? gst / 2 : 0;
        const sgst = form.supplyType === "INTRA" ? gst / 2 : 0;
        const igst = form.supplyType === "INTER" ? gst : 0;

        return {
          taxable: acc.taxable + taxable,
          cgst: acc.cgst + cgst,
          sgst: acc.sgst + sgst,
          igst: acc.igst + igst,
          tax: acc.tax + gst,
          total: acc.total + taxable + gst,
        };
      },
      { taxable: 0, cgst: 0, sgst: 0, igst: 0, tax: 0, total: 0 },
    );
  }, [form.details, form.supplyType]);

  const save = async () => {
    if (!form.companyId) return alert("Company is required");
    if (!form.partyAccountId) return alert("Party Account is required");
    if (!form.taxableAccountId) return alert("Taxable Account is required");

    try {
      setSaving(true);

      const payload = {
        ...form,
        companyId: Number(form.companyId),
        partyAccountId: Number(form.partyAccountId),
        taxableAccountId: Number(form.taxableAccountId),
        details: form.details.map((x) => ({
          hsnCode: x.hsnCode,
          taxableAmount: Number(x.taxableAmount || 0),
          gstPercent: Number(x.gstPercent || 0),
        })),
      };

      const res = await client.post("/gstvouchers", payload);

      alert(res.data?.Message || res.data?.message || "GST voucher saved");

      setForm((prev) => ({
        ...prev,
        voucherNo: "",
        narration: "",
        partyAccountId: "",
        taxableAccountId: "",
        details: [{ ...emptyLine }],
      }));

      await load();
    } catch (e) {
      alert(
        e?.response?.data?.message ||
          e?.response?.data ||
          e.message ||
          "Failed to save GST voucher",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h6 style={styles.title}>
            <i className="bi bi-receipt-cutoff me-2"></i>
            GST Voucher
          </h6>
          <div style={styles.sub}>
            Manual GST voucher posting with CGST / SGST / IGST split and journal
            entry.
          </div>
        </div>

        <div style={styles.body}>
          <div className="row g-2 mb-3">
            <div className="col-md-2">
              <label style={styles.label}>Company Id</label>
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.companyId}
                onChange={(e) =>
                  setForm({ ...form, companyId: e.target.value })
                }
              />
            </div>

            <div className="col-md-2">
              <label style={styles.label}>Voucher No</label>
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.voucherNo}
                onChange={(e) =>
                  setForm({ ...form, voucherNo: e.target.value })
                }
              />
            </div>

            <div className="col-md-2">
              <label style={styles.label}>Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.voucherDate}
                onChange={(e) =>
                  setForm({ ...form, voucherDate: e.target.value })
                }
              />
            </div>

            <div className="col-md-2">
              <label style={styles.label}>Type</label>
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.voucherType}
                onChange={(e) =>
                  setForm({ ...form, voucherType: e.target.value })
                }
              >
                <option value="OUTPUT">OUTPUT</option>
                <option value="INPUT">INPUT</option>
              </select>
            </div>

            <div className="col-md-2">
              <label style={styles.label}>Supply</label>
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.supplyType}
                onChange={(e) =>
                  setForm({ ...form, supplyType: e.target.value })
                }
              >
                <option value="INTRA">INTRA</option>
                <option value="INTER">INTER</option>
              </select>
            </div>

            <div className="col-md-2 d-grid">
              <label style={styles.label}>&nbsp;</label>
              <button
                className="btn btn-outline-dark btn-sm"
                style={styles.btn}
                onClick={load}
                disabled={loading}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                {loading ? "Loading..." : "Refresh"}
              </button>
            </div>
          </div>

          <div className="row g-2 mb-3">
            <div className="col-md-4">
              <label style={styles.label}>Party Account</label>
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.partyAccountId}
                onChange={(e) =>
                  setForm({ ...form, partyAccountId: e.target.value })
                }
              >
                <option value="">Select</option>
                {accounts.map((a) => (
                  <option key={getId(a)} value={getId(a)}>
                    {getCode(a)} - {getName(a)}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4">
              <label style={styles.label}>Taxable Account</label>
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.taxableAccountId}
                onChange={(e) =>
                  setForm({ ...form, taxableAccountId: e.target.value })
                }
              >
                <option value="">Select</option>
                {accounts.map((a) => (
                  <option key={getId(a)} value={getId(a)}>
                    {getCode(a)} - {getName(a)}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-4">
              <label style={styles.label}>Narration</label>
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.narration}
                onChange={(e) =>
                  setForm({ ...form, narration: e.target.value })
                }
              />
            </div>
          </div>

          <div className="table-responsive mb-2">
            <table className="table table-sm table-bordered align-middle mb-0">
              <thead>
                <tr>
                  <th style={styles.th}>HSN</th>
                  <th style={styles.th}>Taxable</th>
                  <th style={styles.th}>GST %</th>
                  <th style={styles.th}>CGST</th>
                  <th style={styles.th}>SGST</th>
                  <th style={styles.th}>IGST</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {form.details.map((line, index) => {
                  const taxable = Number(line.taxableAmount || 0);
                  const gst = (taxable * Number(line.gstPercent || 0)) / 100;
                  const cgst = form.supplyType === "INTRA" ? gst / 2 : 0;
                  const sgst = form.supplyType === "INTRA" ? gst / 2 : 0;
                  const igst = form.supplyType === "INTER" ? gst : 0;

                  return (
                    <tr key={index}>
                      <td style={styles.td}>
                        <input
                          className="form-control form-control-sm"
                          style={styles.input}
                          value={line.hsnCode}
                          onChange={(e) =>
                            setLine(index, "hsnCode", e.target.value)
                          }
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="number"
                          className="form-control form-control-sm text-end"
                          style={styles.input}
                          value={line.taxableAmount}
                          onChange={(e) =>
                            setLine(index, "taxableAmount", e.target.value)
                          }
                        />
                      </td>
                      <td style={styles.td}>
                        <input
                          type="number"
                          className="form-control form-control-sm text-end"
                          style={styles.input}
                          value={line.gstPercent}
                          onChange={(e) =>
                            setLine(index, "gstPercent", e.target.value)
                          }
                        />
                      </td>
                      <td className="text-end" style={styles.td}>
                        {cgst.toFixed(2)}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {sgst.toFixed(2)}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {igst.toFixed(2)}
                      </td>
                      <td style={styles.td}>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => removeLine(index)}
                          disabled={form.details.length === 1}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="row g-2 mb-3">
            {[
              ["Taxable", totals.taxable],
              ["CGST", totals.cgst],
              ["SGST", totals.sgst],
              ["IGST", totals.igst],
              ["GST", totals.tax],
              ["Total", totals.total],
            ].map(([label, value]) => (
              <div className="col-md-2" key={label}>
                <div style={styles.stat}>
                  <div className="text-muted" style={{ fontSize: 10 }}>
                    {label}
                  </div>
                  <div className="fw-bold text-primary">
                    ₹ {value.toFixed(2)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="d-flex gap-2 mb-3">
            <button
              className="btn btn-outline-secondary btn-sm"
              style={styles.btn}
              onClick={() =>
                setForm({
                  ...form,
                  details: [...form.details, { ...emptyLine }],
                })
              }
            >
              <i className="bi bi-plus-circle me-1"></i>
              Add Line
            </button>

            <button
              className="btn btn-primary btn-sm"
              style={styles.btn}
              onClick={save}
              disabled={saving}
            >
              <i className="bi bi-save me-1"></i>
              {saving ? "Saving..." : "Save GST Voucher"}
            </button>
          </div>

          <hr />

          <h6 className="fw-bold">Recent GST Vouchers</h6>

          <div className="table-responsive">
            <table className="table table-sm table-bordered table-hover">
              <thead>
                <tr>
                  <th style={styles.th}>Voucher No</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Supply</th>
                  <th style={styles.th}>Taxable</th>
                  <th style={styles.th}>Tax</th>
                  <th style={styles.th}>Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.length ? (
                  rows.map((r, i) => (
                    <tr key={i}>
                      <td style={styles.td}>{r.VoucherNo ?? r.voucherNo}</td>
                      <td style={styles.td}>
                        {String(r.VoucherDate ?? r.voucherDate).slice(0, 10)}
                      </td>
                      <td style={styles.td}>
                        {r.VoucherType ?? r.voucherType}
                      </td>
                      <td style={styles.td}>{r.SupplyType ?? r.supplyType}</td>
                      <td className="text-end" style={styles.td}>
                        {Number(
                          r.TaxableAmount ?? r.taxableAmount ?? 0,
                        ).toFixed(2)}
                      </td>
                      <td className="text-end" style={styles.td}>
                        {Number(r.TaxAmount ?? r.taxAmount ?? 0).toFixed(2)}
                      </td>
                      <td className="text-end fw-bold" style={styles.td}>
                        {Number(r.GrandTotal ?? r.grandTotal ?? 0).toFixed(2)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center text-muted py-3">
                      No GST vouchers found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
